/* eslint-disable consistent-return,max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/es';
import SweetAlert from 'react-bootstrap-sweetalert';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import SupervisedUser from '@material-ui/icons/SupervisedUserCircle';
import User from '@material-ui/icons/AccountCircle';
import Clear from '@material-ui/icons/Close';
// @material-ui/core components
import FilterStyles from 'assets/jss/AdsFilter';
import Switch from '@material-ui/core/Switch/Switch';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import {
  schedules,
  frequencies,
  paidAdStatus,
  pendingReasonOptions,
  userRanks,
} from 'config';
import Datepicker from 'components/common/DatePicker';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import UserSelect from 'components/User/Select';
import AuthContext from 'helpers/authContext';

/**
 * Variable to define default selected status
 */
const defaultSelectedStatus = [
  paidAdStatus.PENDING,
  paidAdStatus.RUNNING,
  paidAdStatus.COMPLETED,
  paidAdStatus.PAUSED,
  paidAdStatus.CANCELLED,
  paidAdStatus.SCHEDULED,
  paidAdStatus.NO_CREDIT,
];

/**
 * Variable to define default selected frequency
 */
const defaultSelectedFrequency = [
  frequencies.ONEMINUTES,
  frequencies.TWOHOURS,
  frequencies.FOURHOURS,
  frequencies.EIGHTHOURS,
  frequencies.TWELVEHOURS,
];

class PaidAdFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      selectedPaidAdsStatus:
        (appliedFilter && appliedFilter.status) || defaultSelectedStatus,
      selectedFrequency:
        (appliedFilter && appliedFilter.frequency) || defaultSelectedFrequency,
      localFilter: appliedFilter || null,
      showSelectAgency: false,
      showSelectOwner: false,
    };
  }

  /**
   * @description
   * Change filters.
   *
   * @param {Object} changedFilter - Changed filters values.
   * @param {*} callback - Callback waiting for setState.
   */
  handleChangeFilter = (changedFilter, callback = null) => {
    const { localFilter } = this.state;
    this.setState(
      { localFilter: { ...localFilter, ...changedFilter } },
      callback,
    );
  };

  /**
   * @description
   * Update selected status.
   *
   * @param {*} callback - Callback waiting for setState.
   */
  updateSelectedCheckboxes = callback => {
    const { selectedPaidAdsStatus } = this.state;
    this.handleChangeFilter(
      selectedPaidAdsStatus.length === 0
        ? { status: undefined }
        : { status: selectedPaidAdsStatus },
      callback,
    );
  };

  /**
   * @description
   * Update selected status.
   *
   * @param {*} callback - Callback waiting for setState.
   */
  updateFrequencyCheckboxes = callback => {
    const { selectedFrequency } = this.state;
    this.handleChangeFilter(
      selectedFrequency.length === 5 || selectedFrequency.length === 0
        ? { frequency: undefined }
        : { frequency: selectedFrequency },
      callback,
    );
  };

  /**
   * @description
   * Handle frequency checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleFrequencyCheck = (checkLabel, callback = null) => {
    const { selectedFrequency } = this.state;
    if (selectedFrequency.indexOf(checkLabel) !== -1) {
      const newSelected = selectedFrequency.filter(
        frequency => JSON.stringify(frequency) !== JSON.stringify(checkLabel),
      );
      this.setState({ selectedFrequency: newSelected }, () =>
        this.updateFrequencyCheckboxes(callback),
      );
    } else {
      this.setState(
        {
          selectedFrequency: [...selectedFrequency, checkLabel],
        },
        () => this.updateFrequencyCheckboxes(callback),
      );
    }
  };

  /**
   * @description
   * Handle select status checkboxes.
   *
   * @param {string} checkLabel - Status selected.
   * @param {*} callback - Callback waiting for setState.
   * @returns {undefined} No return.
   */
  handleStatusCheck = (checkLabel, callback = null) => {
    const { selectedPaidAdsStatus } = this.state;
    if (selectedPaidAdsStatus.indexOf(checkLabel) !== -1) {
      const newSelected = selectedPaidAdsStatus.filter(
        status => status !== checkLabel,
      );
      this.setState({ selectedPaidAdsStatus: newSelected }, () =>
        this.updateSelectedCheckboxes(callback),
      );
    } else {
      this.setState(
        {
          selectedPaidAdsStatus: [...selectedPaidAdsStatus, checkLabel],
        },
        () => this.updateSelectedCheckboxes(callback),
      );
    }
  };

  handleSelectedAgency = agency => {
    const { localFilter, showSelectAgency } = this.state;

    let oldOwner = localFilter.owner;
    if (localFilter.manager && agency.id !== localFilter.manager) {
      oldOwner = undefined;
    }

    // change state
    this.setState({
      showSelectAgency: !showSelectAgency,
      localFilter: {
        ...localFilter,
        manager: agency.id,
        managerName: agency.fullname,
        owner: oldOwner,
      },
    });
  };

  /**
   * @description
   * Call filter callback.
   */
  handleFilter = () => {
    const { filter } = this.props;
    const { localFilter } = this.state;
    filter({ ...localFilter });
  };

  /**
   * @description
   * Format frequency and duration received from the server.
   *
   * @param {Object} param - Frequency or duration received from the server.
   * @param {Object} i18n - Translation.
   * @returns {Object} Converted and translated frequency and duration.
   */
  changeFormatToString = (
    { years, months, days, hours, minutes, seconds },
    i18n,
  ) => {
    moment.locale(i18n.language);
    return moment()
      .add({ seconds, minutes, hours, days, months, years })
      .fromNow(true);
  };

  /**
   * @description
   * Render full filter's box.
   * @returns {*} - AdFilter's box.
   */
  render() {
    const { classes, restartFilters, t, i18n, showFilters } = this.props;
    const {
      selectedPaidAdsStatus,
      selectedFrequency,
      localFilter,
      showSelectOwner,
      showSelectAgency,
    } = this.state;
    return (
      <AuthContext.Consumer>
        {/* eslint-disable-next-line consistent-return */}
        {user => {
          const hasGeneral =
            user.allPermissions.filter(
              permission => permission.codename === 'list_autopaidad',
            ).length === 1;
          return (
            <div className={classes.expandFilters} style={{ marginBottom: 20 }}>
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <SectionDivider label={t('paidad.filters.section.ad')}>
                    <div style={{ padding: 10 }}>
                      <GridContainer>
                        <GridItem>
                          <GridContainer>
                            <GridItem xs={12} md={4}>
                              <CustomInput
                                data-cy="adsFilterId"
                                id="id"
                                formControlProps={{
                                  className: `${classes.top} ${classes.search}`,
                                }}
                                inputProps={{
                                  placeholder: t(
                                    'paidad.filters.ad.body.id_placeholder',
                                  ),
                                  inputProps: {
                                    'aria-label': 'Search',
                                    className: classes.searchInput,
                                  },
                                  onChange: e => {
                                    const { value } = e.target;
                                    this.handleChangeFilter({
                                      ad: value || undefined,
                                    });
                                  },
                                  value:
                                    localFilter.ad !== undefined
                                      ? localFilter.ad
                                      : '',
                                  type: 'number',
                                }}
                              />
                            </GridItem>
                            <GridItem xs={12} md={4}>
                              <CustomInput
                                data-cy="adsFilterPaidAdId"
                                id="paidadId"
                                formControlProps={{
                                  className: `${classes.top} ${classes.search}`,
                                }}
                                inputProps={{
                                  placeholder: t(
                                    'paidad.filters.ad.body.paidadId_placeholder',
                                  ),
                                  inputProps: {
                                    'aria-label': 'Search',
                                    className: classes.searchInput,
                                  },
                                  onChange: e => {
                                    const { value } = e.target;
                                    this.handleChangeFilter({
                                      id: value || undefined,
                                    });
                                  },
                                  value:
                                    localFilter.id !== undefined
                                      ? localFilter.id
                                      : '',
                                  type: 'number',
                                }}
                              />
                            </GridItem>
                            <GridItem xs={12} md={4}>
                              <div>
                                <span>
                                  {t('paidad.filters.ad.body.only_promotion')}
                                </span>
                                <Switch
                                  id="promotion-switch"
                                  checked={
                                    localFilter.hasPromotion !== undefined
                                  }
                                  onChange={() => {
                                    if (localFilter.hasPromotion === true) {
                                      this.handleChangeFilter({
                                        hasPromotion: undefined,
                                      });
                                    } else
                                      this.handleChangeFilter({
                                        hasPromotion: true,
                                      });
                                  }}
                                  classes={{
                                    checked: classes.switchChecked,
                                    icon: classes.switchIcon,
                                    iconChecked: classes.switchIconChecked,
                                    bar: classes.switchBar,
                                  }}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                          <GridContainer>
                            <GridItem>
                              <h6 className={classes.title}>
                                {t('paidad.filters.ad.body.schedule_title')}
                              </h6>
                              <div className={classes.buttonGroup}>
                                <Button
                                  color="info"
                                  simple={localFilter.schedule !== undefined}
                                  round
                                  className={classes.firstButton}
                                  onClick={() =>
                                    this.handleChangeFilter({
                                      schedule: undefined,
                                    })
                                  }
                                  id="schedule-all"
                                  key="schedule-all"
                                >
                                  {t('paidad.filters.ad.body.schedule.all')}
                                </Button>
                                <Button
                                  simple={
                                    localFilter.schedule === undefined ||
                                    localFilter.schedule[0] !==
                                      schedules.FROM12AM_TO12AM
                                  }
                                  color="info"
                                  round
                                  className={classes.middleButton}
                                  onClick={() =>
                                    this.handleChangeFilter({
                                      schedule: [schedules.FROM12AM_TO12AM],
                                    })
                                  }
                                  id="schedule-day"
                                  key="schedule-day"
                                >
                                  {t('paidad.filters.ad.body.schedule.day')}
                                </Button>
                                <Button
                                  color="info"
                                  round
                                  className={classes.lastButton}
                                  simple={
                                    localFilter.schedule === undefined ||
                                    localFilter.schedule[0] !==
                                      schedules.FROM8AM_TO12AM
                                  }
                                  onClick={() =>
                                    this.handleChangeFilter({
                                      schedule: [schedules.FROM8AM_TO12AM],
                                    })
                                  }
                                  id="schedule-partial"
                                  key="schedule-partial"
                                >
                                  {t('paidad.filters.ad.body.schedule.partial')}
                                </Button>
                              </div>
                            </GridItem>
                            <GridItem>
                              <h6 className={classes.title}>
                                {t('paidad.filters.ad.body.frequency_title')}
                              </h6>
                              <GridContainer>
                                <GridItem xs={12} md={6}>
                                  <GridItem>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          className={classes.checked}
                                          onClick={() =>
                                            this.handleFrequencyCheck(
                                              frequencies.ONEMINUTES,
                                            )
                                          }
                                          checked={
                                            selectedFrequency.indexOf(
                                              frequencies.ONEMINUTES,
                                            ) !== -1
                                          }
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                        />
                                      }
                                      id="frequencyOneMinuteCheckbox"
                                      label={t(
                                        `paidad.filters.ad.body.frequency`,
                                        {
                                          frequency: this.changeFormatToString(
                                            frequencies.ONEMINUTES,
                                            i18n,
                                          ),
                                        },
                                      )}
                                      style={{ width: '100%' }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          className={classes.checked}
                                          onClick={() =>
                                            this.handleFrequencyCheck(
                                              frequencies.TWOHOURS,
                                            )
                                          }
                                          checked={
                                            selectedFrequency.indexOf(
                                              frequencies.TWOHOURS,
                                            ) !== -1
                                          }
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                        />
                                      }
                                      id="frequencyTwoHoursCheckbox"
                                      label={t(
                                        `paidad.filters.ad.body.frequency`,
                                        {
                                          frequency: this.changeFormatToString(
                                            frequencies.TWOHOURS,
                                            i18n,
                                          ),
                                        },
                                      )}
                                      style={{ width: '100%' }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          className={classes.checked}
                                          onClick={() =>
                                            this.handleFrequencyCheck(
                                              frequencies.FOURHOURS,
                                            )
                                          }
                                          checked={
                                            selectedFrequency.indexOf(
                                              frequencies.FOURHOURS,
                                            ) !== -1
                                          }
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                        />
                                      }
                                      id="frequencyFourHoursCheckbox"
                                      label={t(
                                        `paidad.filters.ad.body.frequency`,
                                        {
                                          frequency: this.changeFormatToString(
                                            frequencies.FOURHOURS,
                                            i18n,
                                          ),
                                        },
                                      )}
                                      style={{ width: '100%' }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          className={classes.checked}
                                          onClick={() =>
                                            this.handleFrequencyCheck(
                                              frequencies.EIGHTHOURS,
                                            )
                                          }
                                          checked={
                                            selectedFrequency.indexOf(
                                              frequencies.EIGHTHOURS,
                                            ) !== -1
                                          }
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                        />
                                      }
                                      id="frequencyEightHoursCheckbox"
                                      label={t(
                                        `paidad.filters.ad.body.frequency`,
                                        {
                                          frequency: this.changeFormatToString(
                                            frequencies.EIGHTHOURS,
                                            i18n,
                                          ),
                                        },
                                      )}
                                      style={{ width: '100%' }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          className={classes.checked}
                                          onClick={() =>
                                            this.handleFrequencyCheck(
                                              frequencies.TWELVEHOURS,
                                            )
                                          }
                                          checked={
                                            selectedFrequency.indexOf(
                                              frequencies.TWELVEHOURS,
                                            ) !== -1
                                          }
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                        />
                                      }
                                      id="frequencyTwelveHoursCheckbox"
                                      label={t(
                                        `paidad.filters.ad.body.frequency`,
                                        {
                                          frequency: this.changeFormatToString(
                                            frequencies.TWELVEHOURS,
                                            i18n,
                                          ),
                                        },
                                      )}
                                      style={{ width: '100%' }}
                                    />
                                  </GridItem>
                                </GridItem>
                                <GridItem xs={12} md={6}>
                                  {(hasGeneral ||
                                    user.rank === userRanks.ADMIN) && (
                                    <div>
                                      <div
                                        className={`${classes.userSelect} ${
                                          localFilter.manager
                                            ? classes.userSelected
                                            : ''
                                        }`}
                                      >
                                        <Button
                                          id="selectManager"
                                          simple
                                          color="info"
                                          onClick={() => {
                                            this.setState({
                                              showSelectAgency: !showSelectAgency,
                                            });
                                          }}
                                        >
                                          <div>
                                            <p className="title">
                                              {t('paidad.filters.manager')}
                                            </p>
                                            <p className="user">
                                              {localFilter.managerName
                                                ? localFilter.managerName
                                                : t(
                                                    'paidad.filters.select_placeholder',
                                                  )}
                                            </p>
                                          </div>
                                        </Button>

                                        {localFilter.manager ? (
                                          <Button
                                            simple
                                            onClick={() => {
                                              this.handleChangeFilter({
                                                manager: undefined,
                                                managerName: undefined,
                                                owner: undefined,
                                                ownerName: undefined,
                                              });
                                            }}
                                          >
                                            <Clear />
                                          </Button>
                                        ) : (
                                          <span>
                                            <SupervisedUser />
                                          </span>
                                        )}
                                      </div>

                                      {showSelectAgency && (
                                        <SweetAlert
                                          title=""
                                          showConfirm={false}
                                          showCancel={false}
                                          onConfirm={() => {}}
                                          customClass={
                                            classes.adModalConfirmation
                                          }
                                          show={showSelectAgency}
                                          closeOnClickOutside
                                          onCancel={() =>
                                            this.setState({
                                              showSelectAgency: !showSelectAgency,
                                            })
                                          }
                                        >
                                          <UserSelect
                                            listAgencies
                                            selectUser={
                                              this.handleSelectedAgency
                                            }
                                          />
                                        </SweetAlert>
                                      )}
                                    </div>
                                  )}
                                  <div style={{ marginTop: 20 }}>
                                    <div
                                      className={`${classes.userSelect} ${
                                        localFilter.owner
                                          ? classes.userSelected
                                          : ''
                                      }`}
                                    >
                                      <Button
                                        simple
                                        color="info"
                                        onClick={() => {
                                          this.setState({
                                            showSelectOwner: !showSelectOwner,
                                          });
                                        }}
                                      >
                                        <span>
                                          <p className="title">
                                            {t('paidad.filters.owner')}
                                          </p>
                                          <p className="user">
                                            {localFilter.ownerName
                                              ? localFilter.ownerName
                                              : t(
                                                  'paidad.filters.select_placeholder',
                                                )}
                                          </p>
                                        </span>
                                      </Button>

                                      {localFilter.owner ? (
                                        <Button
                                          simple
                                          onClick={() => {
                                            this.handleChangeFilter({
                                              owner: undefined,
                                              ownerName: undefined,
                                            });
                                          }}
                                        >
                                          <Clear />
                                        </Button>
                                      ) : (
                                        <span>
                                          <User />
                                        </span>
                                      )}
                                    </div>

                                    <SweetAlert
                                      title=""
                                      showConfirm={false}
                                      showCancel={false}
                                      onConfirm={() => {}}
                                      customClass={classes.adModalConfirmation}
                                      show={showSelectOwner}
                                      closeOnClickOutside
                                      onCancel={() =>
                                        this.setState({
                                          showSelectOwner: !showSelectOwner,
                                        })
                                      }
                                    >
                                      <UserSelect
                                        agency={
                                          localFilter.manager
                                            ? {
                                                id: localFilter.manager,
                                              }
                                            : {}
                                        }
                                        selectUser={owner => {
                                          this.setState({
                                            localFilter: {
                                              ...localFilter,
                                              owner: owner.id,
                                              ownerName: owner.fullname,
                                            },
                                            showSelectOwner: !showSelectOwner,
                                          });
                                        }}
                                      />
                                    </SweetAlert>
                                  </div>
                                </GridItem>
                              </GridContainer>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                      </GridContainer>
                    </div>
                  </SectionDivider>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <SectionDivider label={t('paidad.filters.section.date')}>
                    <GridContainer>
                      <GridItem xs={12} sm={12} md={6}>
                        <Datepicker
                          title={t('paidad.filters.date.from')}
                          defaultValue={t(
                            'paidad.filters.date.from_placeholder',
                          )}
                          onChange={date => {
                            if (localFilter.startDateLte < date) {
                              return;
                            }
                            this.handleChangeFilter({
                              startDateGte: date
                                ? moment(date)
                                    .startOf('day')
                                    .format()
                                : undefined,
                            });
                          }}
                          value={
                            localFilter.startDateGte !== undefined
                              ? moment(localFilter.startDateGte)
                              : ''
                          }
                          validGte={localFilter.startDateLte}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={12} md={6}>
                        <Datepicker
                          title={t('paidad.filters.date.to')}
                          defaultValue={t('paidad.filters.date.to_placeholder')}
                          onChange={date => {
                            if (localFilter.startDateGte > date) {
                              return;
                            }
                            this.handleChangeFilter({
                              startDateLte: date
                                ? moment(date)
                                    .endOf('day')
                                    .format()
                                : undefined,
                            });
                          }}
                          value={
                            localFilter.startDateLte !== undefined
                              ? moment(localFilter.startDateLte)
                              : ''
                          }
                          validLte={localFilter.startDateGte}
                        />
                      </GridItem>
                    </GridContainer>
                  </SectionDivider>
                  <SectionDivider label={t('paidad.filters.section.status')}>
                    <GridItem>
                      <GridContainer>
                        <GridItem xs={12} md={8}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.PAUSED)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.PAUSED,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="pausedCheckbox"
                            label={t('paidad.filters.status.paused')}
                            style={{ width: '100%' }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.RUNNING)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.RUNNING,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="runningCheckbox"
                            label={t('paidad.filters.status.running')}
                            style={{ width: '100%' }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() => {
                                  this.handleStatusCheck(paidAdStatus.PENDING);
                                  this.handleChangeFilter({
                                    pendingReason: undefined,
                                  });
                                }}
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.PENDING,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="pendingCheckbox"
                            label={t('paidad.filters.status.pending')}
                            style={{ width: '100%' }}
                          />
                        </GridItem>
                        <GridItem xs={12} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.COMPLETED)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.COMPLETED,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="completedCheckbox"
                            label={t('paidad.filters.status.completed')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.CANCELLED)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.CANCELLED,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="cancelledCheckbox"
                            label={t('paidad.filters.status.cancelled')}
                            style={{ width: '100%' }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.SCHEDULED)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.SCHEDULED,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="scheduledCheckbox"
                            label={t('paidad.filters.status.scheduled')}
                            style={{ width: '100%' }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                onClick={() =>
                                  this.handleStatusCheck(paidAdStatus.NO_CREDIT)
                                }
                                checked={
                                  selectedPaidAdsStatus.indexOf(
                                    paidAdStatus.NO_CREDIT,
                                  ) !== -1
                                }
                                checkedIcon={
                                  <Check className={classes.checkedIcon} />
                                }
                                icon={
                                  <Check className={classes.uncheckedIcon} />
                                }
                                className={classes.checked}
                              />
                            }
                            id="noCreditCheckbox"
                            label={t('paidad.filters.status.no_credit')}
                            style={{ width: '100%' }}
                          />
                        </GridItem>
                        <GridItem xs={12}>
                          <GridContainer justify="flex-end">
                            <GridItem>
                              <Button
                                color="primary"
                                simple
                                className={classes.buttons}
                                onClick={() =>
                                  this.setState(
                                    {
                                      selectedPaidAdsStatus:
                                        selectedPaidAdsStatus.length > 0
                                          ? []
                                          : [
                                              paidAdStatus.PENDING,
                                              paidAdStatus.COMPLETED,
                                              paidAdStatus.RUNNING,
                                              paidAdStatus.PAUSED,
                                              paidAdStatus.CANCELLED,
                                              paidAdStatus.SCHEDULED,
                                              paidAdStatus.NO_CREDIT,
                                            ],
                                    },
                                    this.updateSelectedCheckboxes,
                                  )
                                }
                                id="unselectAll"
                                key="unselectAll"
                              >
                                {selectedPaidAdsStatus.length > 0
                                  ? t('ad.filters.status.uncheck')
                                  : t('ad.filters.status.check')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                        <GridItem xs={12}>
                          <div style={{ position: 'relative' }}>
                            <div
                              className={
                                selectedPaidAdsStatus.indexOf(
                                  paidAdStatus.PENDING,
                                ) === -1
                                  ? classes.disabledContent
                                  : ''
                              }
                            />
                            <h6 className={classes.title}>
                              {t('paidad.filters.status.pending_title')}
                            </h6>
                            <div className={classes.buttonGroup}>
                              <Button
                                simple={
                                  localFilter.pendingReason === undefined ||
                                  localFilter.pendingReason.indexOf(
                                    pendingReasonOptions.AD_UNPUBLISHED,
                                  ) === -1
                                }
                                color="info"
                                round
                                className={classes.firstButton}
                                onClick={() =>
                                  this.handleChangeFilter({
                                    pendingReason: [
                                      pendingReasonOptions.AD_UNPUBLISHED,
                                    ],
                                  })
                                }
                                id="pending-published"
                                key="pending-published"
                              >
                                {t('paidad.filters.status.pending_publishing')}
                              </Button>
                              <Button
                                color="info"
                                simple={localFilter.pendingReason !== undefined}
                                className={classes.middleButton}
                                onClick={() =>
                                  this.handleChangeFilter({
                                    pendingReason: undefined,
                                  })
                                }
                                id="pending-all"
                                key="pending-all"
                              >
                                {t('paidad.filters.status.pending_all')}
                              </Button>
                              <Button
                                simple={
                                  localFilter.pendingReason === undefined ||
                                  localFilter.pendingReason.indexOf(
                                    pendingReasonOptions.AD_UNREVIEWED,
                                  ) === -1
                                }
                                color="info"
                                round
                                className={classes.lastButton}
                                onClick={() =>
                                  this.handleChangeFilter({
                                    pendingReason: [
                                      pendingReasonOptions.AD_UNREVIEWED,
                                    ],
                                  })
                                }
                                id="pending-reviewed"
                                key="pending-reviewed"
                              >
                                {t('paidad.filters.status.pending_reviewed')}
                              </Button>
                            </div>
                          </div>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </SectionDivider>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <Button
                    color="primary"
                    simple
                    className={classes.buttons}
                    onClick={showFilters}
                    id="hideFilters"
                    key="hideFilters"
                  >
                    <ArrowUp />
                    {t('ad.filters.shrink')}
                  </Button>
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <GridContainer justify="flex-end">
                    <GridItem>
                      <Button
                        color="danger"
                        simple
                        className={classes.buttons}
                        onClick={() => {
                          this.setState({
                            selectedPaidAdsStatus: defaultSelectedStatus,
                            selectedFrequency: defaultSelectedFrequency,
                            localFilter: {},
                          });
                          restartFilters();
                        }}
                        id="restartFilters"
                        key="restartFilters"
                      >
                        {t('ad.filters.clean')}
                      </Button>
                      <Button
                        data-cy="filterAds"
                        color="primary"
                        className={classes.buttons}
                        onClick={() => {
                          this.handleFilter();
                          showFilters();
                        }}
                        id="filterAds"
                        key="filterAds"
                      >
                        {t('ad.filters.filter')}
                      </Button>
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </div>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

/**
 * PaidAdFilter PropTypes
 * @param classes - styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user clicks filters button
 * @callback restartFilters - callback called when user restart filters button
 */

PaidAdFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  showFilters: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
};

export default withTranslation()(withStyles(FilterStyles)(PaidAdFilter));
